import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";

const ExportProducts = () => {
  const { t } = useTranslation();

  const categories = [
    {
      id: 1,
      name: t('exportProducts.categories.kingCoconutWater.name'),
      image: "/images/categories/king-coconut-water.webp",
      link: "/products/king-coconut-water",
      description: t('exportProducts.categories.kingCoconutWater.description'),
    },
    {
      id: 2,
      name: t('exportProducts.categories.cannedBottleFruit.name'),
      image: "/images/categories/canned-bottle-fruit.webp",
      link: "/products/canned-bottle-fruit",
      description: t('exportProducts.categories.cannedBottleFruit.description'),
    },
    {
      id: 3,
      name: t('exportProducts.categories.dehydratedFruits.name'),
      image: "/images/categories/dehydrated-fruits.webp",
      link: "/products/dehydrated-fruits",
      description: t('exportProducts.categories.dehydratedFruits.description'),
    },
    {
      id: 4,
      name: t('exportProducts.categories.fruitJuicePulp.name'),
      image: "/images/categories/fruit-juice-pulp.webp",
      link: "/products/fruit-juice-pulp",
      description: t('exportProducts.categories.fruitJuicePulp.description'),
    },
    {
      id: 5,
      name: t('exportProducts.categories.fruitPowder.name'),
      image: "/images/categories/fruit-powder.webp",
      link: "/products/fruit-powder",
      description: t('exportProducts.categories.fruitPowder.description'),
    },
    {
      id: 6,
      name: t('exportProducts.categories.jackfruitBananaBlossom.name'),
      image: "/images/categories/jackfruit-banana-blossom.webp",
      link: "/products/jackfruit-banana-blossom",
      description: t('exportProducts.categories.jackfruitBananaBlossom.description'),
    },
    {
      id: 7,
      name: t('exportProducts.categories.spices.name'),
      image: "/images/categories/spices.webp",
      link: "/products/spices",
      description: t('exportProducts.categories.spices.description'),
    },
  ];

  const floatingFruits = [
    { emoji: "🍍", delay: 0, duration: 6, x: "10%", y: "20%" },
    { emoji: "🥭", delay: 1, duration: 7, x: "80%", y: "15%" },
    { emoji: "🥥", delay: 2, duration: 8, x: "15%", y: "70%" },
    { emoji: "🍌", delay: 1.5, duration: 6.5, x: "85%", y: "65%" },
    { emoji: "🍍", delay: 3, duration: 7.5, x: "50%", y: "10%" },
    { emoji: "🥭", delay: 2.5, duration: 6, x: "45%", y: "80%" },
  ];

  return (
    <section className="relative py-20 bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 overflow-hidden">
      {/* Wave Animation Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <svg
          className="absolute bottom-0 w-full h-48 text-green-100 dark:text-dark-800 opacity-50"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <motion.path
            fill="currentColor"
            d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,144C960,149,1056,139,1152,128C1248,117,1344,107,1392,101.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            animate={{
              d: [
                "M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,144C960,149,1056,139,1152,128C1248,117,1344,107,1392,101.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
                "M0,128L48,138.7C96,149,192,171,288,165.3C384,160,480,128,576,128C672,128,768,160,864,165.3C960,171,1056,149,1152,133.3C1248,117,1344,107,1392,101.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
                "M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,144C960,149,1056,139,1152,128C1248,117,1344,107,1392,101.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
              ],
            }}
            transition={{
              repeat: Infinity,
              duration: 10,
              ease: "easeInOut",
            }}
          />
        </svg>

        {/* Second Wave */}
        <svg
          className="absolute bottom-0 w-full h-48 text-emerald-100 dark:text-dark-700 opacity-30"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <motion.path
            fill="currentColor"
            d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            animate={{
              d: [
                "M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
                "M0,224L48,213.3C96,203,192,181,288,186.7C384,192,480,224,576,234.7C672,245,768,235,864,213.3C960,192,1056,160,1152,165.3C1248,171,1344,213,1392,234.7L1440,256L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
                "M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
              ],
            }}
            transition={{
              repeat: Infinity,
              duration: 15,
              ease: "easeInOut",
            }}
          />
        </svg>
      </div>

      {/* Floating Fruits */}
      {floatingFruits.map((fruit, index) => (
        <motion.div
          key={index}
          className="absolute text-6xl pointer-events-none opacity-20 dark:opacity-10"
          style={{ left: fruit.x, top: fruit.y }}
          animate={{
            y: [0, -30, 0],
            rotate: [0, 10, -10, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            repeat: Infinity,
            duration: fruit.duration,
            delay: fruit.delay,
            ease: "easeInOut",
          }}
        >
          {fruit.emoji}
        </motion.div>
      ))}

      <div className="container mx-auto px-6 lg:px-16 xl:px-24 relative z-10">
        {/* Main Content Grid: Text Left (2/3), Certifications Right (1/3) */}
        <div className="grid lg:grid-cols-3 gap-16 xl:gap-24 items-start mb-16">
          {/* Left Side - Text Content (2/3 width) */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {t('exportProducts.title')}
            </h2>

            <div className="space-y-2.5 text-xs md:text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              <p>
                {t('exportProducts.intro.paragraph1.part1')}{" "}
                <strong className="text-primary-600 dark:text-primary-400">
                  {t('exportProducts.intro.paragraph1.companyName')}
                </strong>{" "}
                {t('exportProducts.intro.paragraph1.part2')}{" "}
                <span className="text-primary-600 dark:text-primary-400 font-semibold">
                  {t('exportProducts.intro.paragraph1.productList')}
                </span>
                .
              </p>

              <p>
                {t('exportProducts.intro.paragraph2.part1')}{" "}
                <strong>
                  {t('exportProducts.intro.paragraph2.standards')}
                </strong>{" "}
                {t('exportProducts.intro.paragraph2.part2')}
              </p>

              <p>
                {t('exportProducts.intro.paragraph3')}
              </p>

              <p className="text-sm md:text-base font-semibold text-primary-700 dark:text-primary-300">
                {t('exportProducts.intro.guarantee')}
              </p>
            </div>
          </motion.div>

          {/* Right Side - Certifications (1/3 width) */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-1 lg:sticky lg:top-24"
          >
            
          </motion.div>
        </div>

        {/* Product Categories Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={category.link}
                  className="group relative block overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  {/* Image Container */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  </div>

                  {/* Content Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-primary-400 transition-colors duration-300">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-200 mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {category.description}
                    </p>
                    <div className="flex items-center text-primary-400 font-semibold">
                      <span className="mr-2">{t('exportProducts.explore')}</span>
                      <FaArrowRight className="transform group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>

                  {/* Hover Border Effect */}
                  <div className="absolute inset-0 border-4 border-primary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl pointer-events-none" />
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

export default ExportProducts;
